import { useEffect } from 'react';
import { stripHash } from '../utils/anchors';

// Wait a bit after the target appears before scrolling, so mount/entrance
// animations (e.g. the IntersectionObserver-driven "visible" classes used
// across components) have started and layout has settled.
const SCROLL_DELAY_MS = 400;

// Scrolls to the element matching the URL hash (e.g. "#region") once it
// exists in the DOM. Needed because page content on this project can render
// asynchronously (fetched from an API after mount), so the browser's native
// scroll-to-fragment on initial load happens before the target element
// exists and never retries.
export default function useScrollToHash() {
    useEffect(() => {
        const { hash } = window.location;
        if (!hash || hash.length <= 1) {
            return;
        }
        // Legacy content can carry malformatted double/triple "#" anchors
        // (e.g. "##kontakt"); normalize down to the actual element id.
        const id = stripHash(decodeURIComponent(hash.slice(1)));
        if (!id) {
            return;
        }

        let scrollTimeout;
        const scrollToElement = (el) => {
            scrollTimeout = setTimeout(() => {
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, SCROLL_DELAY_MS);
        };

        const existing = document.getElementById(id);
        if (existing) {
            scrollToElement(existing);
            return () => clearTimeout(scrollTimeout);
        }

        const observer = new MutationObserver(() => {
            const el = document.getElementById(id);
            if (el) {
                observer.disconnect();
                scrollToElement(el);
            }
        });
        observer.observe(document.body, { childList: true, subtree: true });

        // Stop watching if the target never shows up so we don't observe forever.
        const giveUpTimeout = setTimeout(() => observer.disconnect(), 10000);

        return () => {
            observer.disconnect();
            clearTimeout(giveUpTimeout);
            clearTimeout(scrollTimeout);
        };
    }, []);
}
